/**
 * English fallbacks for keys the localization service does not return.
 * A key the service does return overrides these, so a missing key shows this text instead of the raw key.
 */
export const defaultTranslations: Record<string, string> = {
  ACCOUNT_CONNECTED_SUCCESSFULLY: 'account connected successfully.',
  ACTION_NOT_AVAILABLE_FOR_USER_ROLE: 'This action is not available for user role',
  ADD_ACTION: 'Add Action',
  ADD_ACTIONS_FOR_AGENT:
    'Add actions that your agent can perform. External tools, web search, folders, and more.',
  ADD_FILE_OR_FOLDER: 'Add file or folder',
  ADEQUACY_CONTEXT_INSUFFICIENT:
    'The retrieved context may not be enough to answer this question (score: {{score}}).',
  ADEQUACY_CONTEXT_SUFFICIENT:
    'The retrieved context looks sufficient to answer this question (score: {{score}}).',
  AFTER_30_DAYS: 'After 30 days',
  AFTER_7_DAYS: 'After 7 days',
  AGENTS_SELECTED: 'agents selected',
  AGENT_INSTRUCTIONS_PLACEHOLDER:
    'What does this agent do? How should it behave? What should it avoid doing?',
  AGENT_READY_TO_USE: '{{name}} is ready to use!',
  AGENT_SELECTED: 'agent selected',
  AGENT_SHARE_CHANGED_EDITOR_DESCRIPTION: "Your access to '{{agentName}}' is now editor.",
  AGENT_SHARE_CHANGED_EDITOR_UNNAMED_DESCRIPTION: 'Your access to an agent is now editor.',
  AGENT_SHARE_CHANGED_TITLE: 'Agent sharing updated',
  AGENT_SHARE_CHANGED_VIEWER_DESCRIPTION: "Your access to '{{agentName}}' is now viewer.",
  AGENT_SHARE_CHANGED_VIEWER_UNNAMED_DESCRIPTION: 'Your access to an agent is now viewer.',
  AGENT_SHARE_GRANTED_EDITOR_DESCRIPTION: "You were given editor access to '{{agentName}}'.",
  AGENT_SHARE_GRANTED_EDITOR_UNNAMED_DESCRIPTION: 'You were given editor access to an agent.',
  AGENT_SHARE_GRANTED_TITLE: 'Agent shared with you',
  AGENT_SHARE_GRANTED_VIEWER_DESCRIPTION: "You were given viewer access to '{{agentName}}'.",
  AGENT_SHARE_GRANTED_VIEWER_UNNAMED_DESCRIPTION: 'You were given viewer access to an agent.',
  AGENT_SHARE_REVOKED_DESCRIPTION: "Your access to '{{agentName}}' was removed.",
  AGENT_SHARE_REVOKED_TITLE: 'Agent access removed',
  AGENT_SHARE_REVOKED_UNNAMED_DESCRIPTION: 'Your access to an agent was removed.',
  AGENT_TASK_APPROVAL_REQUIRED_DESCRIPTION:
    'A task is waiting for your approval before your agent can work on it.',
  AGENT_TASK_APPROVAL_REQUIRED_TITLE: 'An agent task needs your approval',
  AGENT_TASK_DEADLINE_MISSED_TITLE: 'An agent task is overdue',
  AGENT_TASK_FAILURE_DESCRIPTION: 'A task you take part in failed.',
  AGENT_TASK_FAILURE_TITLE: 'An agent task failed',
  AGENT_TASK_FINAL_DEADLINE_MISSED_DESCRIPTION:
    'No final feedback since {{due}}. Check the task, or reconcile and retry.',
  AGENT_TASK_FINAL_RESULT_DESCRIPTION: 'A task you take part in is complete.',
  AGENT_TASK_FINAL_RESULT_TITLE: 'An agent task is complete',
  AGENT_TASK_PAUSED_DESCRIPTION: '{{reason}}',
  AGENT_TASK_PAUSED_TITLE: 'An agent task is paused',
  AGENT_TASK_PROGRESS_DEADLINE_MISSED_DESCRIPTION:
    'No progress feedback since {{due}}. Check the task, or reconcile and retry.',
  AGENT_TASK_TURN_FAILED_DESCRIPTION: "The agent's turn on this task failed.",
  AGENT_TASK_WAIT_DEADLINE_MISSED_DESCRIPTION:
    'The external confirmation expected by {{expectedBy}} has not arrived; the task is still waiting.',
  ALLOW_POPUPS_TO_CONNECT: 'Please allow popups to connect.',
  ALLOW_USER_CLIENT: 'Allow users to use their own client',
  ALLOW_USER_CLIENT_HINT:
    'Users may sign in through their own app client instead of this one; with this on, the client above is optional.',
  ALL_ACTIONS_DELETED: 'All actions deleted',
  ALL_ACTIONS_DELETED_SUCCESSFULLY: 'All actions deleted successfully.',
  ALL_ACTIONS_UPDATED: 'Actions updated',
  API_ACTION_SAVED_NO_ID: 'The API action was saved but no action ID was returned.',
  API_KEY_OR_TOKEN: 'API key or token',
  API_KEY_OR_TOKEN_REQUIRED: 'API key or token is required',
  ASK_ADMIN_TO_TOP_UP: 'Ask your organization admin to top up.',
  ASSISTANT_DELETED_SUCCESSFULLY: 'Assistant "{{name}}" deleted successfully.',
  ATTACH: 'Attach',
  ATTACH_FILES_AS_KNOWLEDGE: 'Directly attach files to the project and all its chats as knowledge.',
  AUTH_CONFIG: 'auth config',
  AUTH_CONFIG_UNAVAILABLE:
    'No auth configuration is available for this toolkit yet. Contact your administrator.',
  AUTO_SUB_OS_SESSION: 'OS session',
  AUTO_SUB_OS_SESSION_HINT: 'Uses your OS session token — no connection needed.',
  BASE_PROMPT_CANNOT_BE_EMPTY: "Base prompt can't be empty",
  CARD: 'Card',
  CHOOSE_WHAT_HAPPENS_AFTER_USAGE_LIMIT: 'Choose what happens after running into a usage limit.',
  CLICK_TO_PURCHASE_ANOTHER_PLAN: 'Click to purchase another plan',
  CLIENT_ID: 'Client ID',
  CLIENT_ID_REQUIRED: 'Client ID is required',
  CLIENT_SECRET: 'Client Secret',
  CLIENT_SECRET_IF_ANY: 'Client Secret (if your app has one)',
  CLIENT_SECRET_REQUIRED: 'Client Secret is required',
  CLOSE_CHAT: 'Close chat',
  CODE: 'Code',
  COMING_SOON_HEADLINE: 'Coming soon',
  COMPANY_IS_TOO_LONG: 'Company name must be 100 characters or less',
  COMPLETE_AUTH_IN_POPUP:
    'Complete the sign-in in the popup window. This page updates automatically.',
  CONFIRMING_PAYMENT: 'Confirming your payment...',
  CONNECT: 'Connect',
  CONNECTION_FAILED: 'Connection failed',
  CONNECTION_PENDING: 'Pending',
  CONNECTION_REQUIRED: 'Connection required',
  CONTINUE_TO_SIGN_IN: 'Continue to sign in',
  COPY_ACCESS_TOKEN_EMBED_SCRIPT: 'Copy access token embed script',
  COPY_COOKIE_EXAMPLE: 'Copy cookie example',
  COPY_PUBLIC_EMBED_SCRIPT: 'Copy public embed script',
  COULD_NOT_PARSE_CURL: 'Could not parse cURL',
  CREATED_AT: 'Created at',
  CREATED_BY: 'Created by',
  CREATE_SKILL_DISPLAY_NAME_MAX: 'Maximum 128 characters',
  CREATE_SKILL_TOAST_UPDATE_FAILED_TITLE: 'Failed to update skill',
  CREDENTIALS_NOT_SAVED: 'The credentials could not be saved.',
  CREDENTIALS_NOT_SAVED_TITLE: 'Not saved',
  CREDENTIALS_SAVED: 'Credentials saved',
  CSV: 'CSV',
  CURL_COMMAND: 'cURL command',
  CURL_FIELDS_SKIPPED_one: '{{count}} unsupported field skipped',
  CURL_FIELDS_SKIPPED_other: '{{count}} unsupported fields skipped',
  CURL_IMPORTED_REVIEW: 'cURL imported. Review the API details.',
  CURRENT_USER: 'Current user',
  DEFAULT_PROMPT_APPLIED_INSTEAD: 'A default prompt has been applied instead.',
  DELETE_ALL: 'Delete All',
  DELETE_ALL_ACTIONS: 'Delete All Actions',
  DELETE_ALL_ACTIONS_CONFIRMATION:
    'Are you sure you want to delete all APIs for this tool? This cannot be undone.',
  DELETE_LIBRARY_FOLDER_CONFIRMATION:
    'Delete "{{name}}"? This folder and any folders inside it will be removed. Files in them will move back to the library.',
  DEPTH: 'Depth',
  DISABLE_ALL: 'Disable all',
  DISABLE_ALL_ACTIONS: 'Disable all actions',
  DISABLE_ALL_ACTIONS_CONFIRMATION:
    'Turn off {{count}} action(s)? Agents cannot use them until you turn them on again.',
  EMAIL_ADDRESS: 'Email address',
  ENABLE_ALL: 'Enable all',
  ENABLE_ALL_ACTIONS: 'Enable all actions',
  ENABLE_ALL_ACTIONS_CONFIRMATION:
    'Turn on {{count}} action(s)? This includes actions that create, change or delete data. Actions set to require approval still ask before they run.',
  ENTER_API_KEY_OR_TOKEN: 'Enter API key or token',
  ENTER_CLIENT_ID: 'Enter client ID',
  ENTER_CLIENT_SECRET: 'Enter client secret',
  ENTER_FOLDER_NAME: 'Enter folder name',
  ENTER_PHONE_NUMBER: 'Enter phone number',
  ENTER_TOOL_CREDENTIALS: 'Your credentials for {{name}}',
  ERROR_LOADING_ORGANIZATIONS: 'Error loading organizations',
  EXPIRED: 'Expired',
  EXPIRED_ON: 'Expired on: ',
  FAILED_PREVIEW_QUANTITY_CHANGE: 'Failed to preview quantity change',
  FAILED_TO_CREATE_TOOL: 'Failed to create tool',
  FAILED_TO_DELETE_ALL_ACTIONS: 'Failed to delete tool actions',
  FAILED_TO_DELETE_ASSISTANT: 'Failed to delete assistant.',
  FAILED_TO_DISCONNECT: 'Failed to disconnect the account.',
  FAILED_TO_DOWNLOAD_FILE: 'Failed to download file',
  FAILED_TO_GENERATE_FILE: 'Failed to generate {{fileName}}',
  FAILED_TO_MARK_AS_READ: 'Failed to mark notification as read',
  FAILED_TO_START_AUTHORIZATION: 'Failed to start the authorization flow.',
  FAILED_TO_SWITCH_ORGANIZATION: 'Failed to switch organization',
  FAILED_TO_UPDATE_AGENT: 'Failed to update agent',
  FAILED_TO_UPDATE_ALL_ACTIONS: 'Failed to update the actions',
  FAILED_TO_UPDATE_REFERENCE_SAVED_MEMORIES: 'Failed to update the saved memories setting',
  FAILED_TO_UPDATE_TOOL: 'Failed to update tool',
  FAILED_UPDATE_QUANTITY: 'Failed to update seats',
  FILE_GENERATION_FAILED: 'File Generation Failed',
  FILE_MOVED_SUCCESSFULLY: 'File moved successfully',
  FILE_MOVE_FAILED: 'Failed to move file',
  FILE_PROCESSED_TITLE: 'File processed',
  FILE_PROCESSING_FAILED_TITLE: 'File processing failed',
  FILE_UNSHARED_FAILED: 'Failed to unshare the file',
  FILE_UPLOAD_FAILED_DESCRIPTION: "'{{fileName}}' could not be processed.",
  FILE_UPLOAD_NO_EXTRACTABLE_TEXT_DESCRIPTION: "'{{fileName}}' has no text that can be extracted.",
  FILE_UPLOAD_SUCCESS_DESCRIPTION: "'{{fileName}}' was processed successfully.",
  FILTER: 'Filter',
  FOLDER_CREATED_SUCCESSFULLY: 'Folder created successfully',
  FOLDER_CREATE_FAILED: 'Failed to create folder',
  FOLDER_DELETED_FAILED: 'Failed to delete folder',
  FOLDER_DELETED_SUCCESSFULLY: 'Folder deleted successfully',
  FOLDER_ITEM_COUNT: '{{count}} items',
  FOLDER_RENAMED_SUCCESSFULLY: 'Folder renamed successfully',
  GENERATING_TITLE: 'Generating title',
  GRAPHQL_DEPTH_HINT: 'Nested-field expansion depth for generated operations.',
  GRAPHQL_ERRORS: 'GraphQL errors',
  GRAPHQL_QUERY: 'GraphQL Query',
  GRAPHQL_QUERY_HINT:
    'Variables ($name) become typed input parameters; a leading "mutation" marks a write.',
  GREETING: 'Greeting',
  GROUP_BY_AGENT_OR_PROJECT: 'Group by agent or project',
  GROUP_CREATION_FAILED: 'Failed to create the group',
  IMAGE_GENERATION: 'Image Generation',
  IMAGE_GENERATION_ACTION_DESCRIPTION: 'Generates images in the chat using natural language',
  IMPORTING_SCHEMA: 'Importing schema...',
  IMPORTING_SCHEMA_DESCRIPTION: 'Generating operations from the schema. This may take a moment.',
  IMPORT_FROM_CURL: 'Import from cURL',
  IMPORT_FROM_CURL_DESCRIPTION:
    'Paste a cURL command. The request is saved as an API action, then opened so you can review parameters. Output fields cannot be inferred from cURL.',
  IMPORT_FROM_GRAPHQL: 'Import from GraphQL',
  IMPORT_FROM_GRAPHQL_DESCRIPTION:
    'Generate API actions from a GraphQL schema. Each operation is imported as an editable action.',
  IMPORT_SKILL_UNREADABLE_FILE: 'The file could not be read.',
  INTERVAL_DAY: 'day',
  INTERVAL_MONTH: 'month',
  INTERVAL_WEEK: 'week',
  INTERVAL_YEAR: 'year',
  INVALID_CREDENTIALS: 'Invalid credentials',
  INVALID_CURL_COMMAND: 'Invalid cURL command.',
  INVALID_SKILL_FILE: 'Invalid SKILL.md',
  JOINED_GROUP_CHAT_DESCRIPTION: 'You joined the group chat',
  JOINED_GROUP_CHAT_TITLE: 'You joined the group chat',
  KNOWLEDGE: 'Knowledge',
  KNOWLEDGE_BASE_COMPLETED: 'Knowledge base completed',
  KNOWLEDGE_BASE_FAILED: 'Knowledge base failed',
  KNOWLEDGE_BASE_PENDING: 'Knowledge base pending',
  KNOWLEDGE_BASE_PROCESSING: 'Knowledge base processing',
  KNOWLEDGE_BASE_PROCESSING_COMPLETED: 'Knowledge base processing completed.',
  KNOWLEDGE_BASE_PROCESSING_COMPLETED_REASON: 'Knowledge base processing completed: {{reason}}',
  KNOWLEDGE_BASE_PROCESSING_COMPLETED_WITH_CHUNKS:
    'Knowledge base processing completed with {{count}} chunks.',
  KNOWLEDGE_BASE_PROCESSING_COMPLETED_WITH_CHUNKS_one:
    'Knowledge base processing completed with {{count}} chunk.',
  KNOWLEDGE_BASE_PROCESSING_COMPLETED_WITH_CHUNKS_other:
    'Knowledge base processing completed with {{count}} chunks.',
  KNOWLEDGE_BASE_PROCESSING_FAILED: 'Knowledge base processing failed.',
  KNOWLEDGE_BASE_PROCESSING_FAILED_REASON: 'Knowledge base processing failed: {{reason}}',
  KNOWLEDGE_BASE_PROCESSING_IN_PROGRESS: 'Knowledge base processing in progress.',
  KNOWLEDGE_BASE_PROCESSING_IN_PROGRESS_REASON: 'Knowledge base processing in progress: {{reason}}',
  KNOWLEDGE_BASE_PROCESSING_PENDING: 'Knowledge base processing pending.',
  KNOWLEDGE_BASE_PROCESSING_PENDING_REASON: 'Knowledge base processing pending: {{reason}}',
  LABEL_FROM: 'From',
  LEARNED_SKILL_CREATED_DESCRIPTION: "Skill '{{skillName}}' was created.",
  LEARNED_SKILL_CREATED_TITLE: 'Skill learned',
  LEARNED_SKILL_MEMORY_UPDATED_DESCRIPTION: "The agent's memory was updated.",
  LEARNED_SKILL_MEMORY_UPDATED_TITLE: 'Memory updated',
  LEARNED_SKILL_UPDATED_DESCRIPTION: "Skill '{{skillName}}' was updated.",
  LEARNED_SKILL_UPDATED_TITLE: 'Skill updated',
  LOADING_AGENT: 'Loading agent...',
  MAKE_THIS_ASSISTANT_AVAILABLE_TO_ALL_USERS_ADMIN_ONLY:
    'Make this assistant available to all users (Admin Only)',
  MCP_INGEST_FAILED_DESCRIPTION: "MCP service '{{serviceName}}' failed.",
  MCP_INGEST_FAILED_TITLE: 'MCP service ingestion failed',
  MCP_INGEST_NEEDS_AUTH_CONFIRMATION_DESCRIPTION:
    "MCP service '{{serviceName}}' is ready; confirm its authentication.",
  MCP_INGEST_READY_DESCRIPTION: "MCP service '{{serviceName}}' is ready.",
  MCP_INGEST_SUCCESS_TITLE: 'MCP service ingested',
  MCP_SERVICE_AUTH_REQUIRED_DESCRIPTION:
    'This service needs your credentials before the assistant can continue. They are sent securely and never shared with the model.',
  MCP_SERVICE_OIDC_REQUIRED_DESCRIPTION:
    'This service uses single sign-on. Continue to your identity provider to authorize access.',
  MEMORY_DELETED_SUCCESSFULLY: 'Memory deleted successfully',
  MENTION_USER: 'Mention User',
  MESSAGE_SUBAGENT_RUNS: 'Subagent runs',
  MORE_ACTIONS: 'More actions',
  MORE_OPTIONS: 'More options',
  MOVE_FILE_TO_FOLDER_HINT: 'Choose a folder for “{{name}}”.',
  MOVE_TO_FOLDER: 'Move to folder',
  NEW_PROJECT: 'New project',
  NEXT_PAGE: 'Next page',
  NOTIFICATION: 'Notification',
  NO_AGENTS_FOUND_MATCHING_SEARCH: 'No agents found matching your search',
  NO_ASSISTANTS_FOUND: 'No assistants found',
  NO_CONNECTED_SOURCES_AVAILABLE: 'No connected sources available',
  NO_DESCRIPTION_PROVIDED: 'No description provided.',
  NO_EMBEDDING_MODEL: 'No embedding model',
  NO_FILES_MATCH_CRITERIA: 'No files match your criteria',
  NO_QUESTION: 'No question',
  NO_REDIRECT_URL: 'No authorization URL returned.',
  NO_SKILLS_FOUND: 'No skills found',
  NO_SUBAGENTS_AVAILABLE: 'No eligible subagents available',
  OPEN_CHAT: 'Open chat',
  OPEN_MENU: 'Open menu',
  OPTIMIZE: 'Optimize',
  OR: 'OR',
  ORGANIZATIONS: 'Organizations',
  ORGANIZATION_CODE: 'Organization code',
  OR_DRAG_AND_DROP_FILES: 'or drag and drop files here',
  OWN_TOOL_CLIENT_HINT:
    'Your own app registration at the identity provider. Saved for you only and never shared with the model.',
  OWN_TOOL_CLIENT_REMOVED: 'Your client is removed. You sign in through the configured one.',
  OWN_TOOL_CLIENT_REQUIRED:
    'This tool signs you in through your own app client. Enter its client ID, and its client secret if it has one.',
  OWN_TOOL_CLIENT_SAVED: 'Your client is saved. Sign in to use it.',
  PAYMENT_CANCELLED: 'Payment Cancelled',
  PAYMENT_CANCELLED_DESCRIPTION:
    'Your payment was cancelled. No charges were made to your account.',
  PAYMENT_FAILED_DESCRIPTION:
    'We could not process your payment. Please check your payment details and try again.',
  PDF_PREVIEW: 'PDF preview',
  PLAN_LIMIT_TOP_UP_COVERS:
    "Your plan limit is reached. Usage now runs on your organization's top-up credits.",
  PLAN_RENEWS_ON: 'Renews {{date}}',
  POPUP_BLOCKED: 'Popup blocked',
  PPTX: 'PPTX',
  PRESS_HOLD: 'Press and hold',
  PREVIOUS_PAGE: 'Previous page',
  PROJECT_RENAMED_SUCCESSFULLY: 'Project renamed successfully',
  RESEND_MESSAGE: 'Resend message',
  RESPONSE_FAILED_TITLE: 'Response failed',
  ROWS_SELECTED_OF_TOTAL: '{{selected}} of {{total}} row(s) selected.',
  SAVE_CLIENT: 'Save client',
  SAVING: 'Saving...',
  SAVING_API_ACTION: 'Saving API action...',
  SCHEDULED_JOB_AUTO_PAUSED_DESCRIPTION:
    "'{{jobName}}' failed {{failures}} times in a row and has been paused.",
  SCHEDULED_JOB_AUTO_PAUSED_TITLE: 'Scheduled job paused',
  SCHEDULED_JOB_RUN_FAILED_DESCRIPTION: "'{{jobName}}' failed.",
  SCHEDULED_JOB_RUN_FAILED_REASON_DESCRIPTION: "'{{jobName}}' failed. {{reason}}",
  SCHEDULED_JOB_RUN_FAILED_TITLE: 'Scheduled job failed',
  SCHEDULED_JOB_RUN_SUCCEEDED_DESCRIPTION: "'{{jobName}}' completed successfully.",
  SCHEDULED_JOB_RUN_SUCCEEDED_REASON_DESCRIPTION:
    "'{{jobName}}' completed successfully. {{reason}}",
  SCHEDULED_JOB_RUN_SUCCEEDED_TITLE: 'Scheduled job completed successfully',
  SCHEDULED_JOB_RUN_TIMEOUT_DESCRIPTION: "'{{jobName}}' timed out.",
  SCHEDULED_JOB_RUN_TIMEOUT_REASON_DESCRIPTION: "'{{jobName}}' timed out. {{reason}}",
  SCHEDULED_JOB_RUN_TIMEOUT_TITLE: 'Scheduled job timed out',
  SCHEMA_SDL: 'Schema (SDL)',
  SEARCH_ACTIONS: 'Search actions...',
  SELECTED_USERS: 'Selected users',
  SELECT_MODEL_FOR_AGENT: 'Select the model that should power your agent.',
  SHARED: 'Shared',
  SKILLS_TOAST_DOWNLOAD_FAILED: 'Failed to download skill',
  SKILLS_TOAST_DUPLICATED_DESCRIPTION: 'A copy of {{name}} was added to your skills.',
  SKILLS_TOAST_DUPLICATED_TITLE: 'Skill duplicated',
  SKILLS_TOAST_DUPLICATE_FAILED: 'Failed to duplicate skill',
  SKILL_DETAILS_EDIT_TOOLS: 'Edit tools',
  SKILL_DRAFT_FAILED: 'Failed to generate draft',
  SKILL_EDIT_PERMISSION_DENIED: 'You can only edit skills you created.',
  SKILL_OPTIMIZE_FAILED: 'Failed to optimize with AI',
  SOMETHING_WENT_WRONG_SWITCHING_ORGANIZATIONS:
    'Something went wrong while switching organizations. Please try again.',
  SOURCE_SELECTION_FAILED: 'Source selection failed',
  START_CHAT: 'Start chat',
  START_CHATTING_WITH: 'Start chatting with',
  SUBAGENT: 'Subagent',
  SUBAGENT_RUN_COUNT_MANY: '{{count}} runs',
  SUBAGENT_RUN_COUNT_ONE: '1 run',
  SUBAGENT_RUN_DURATION: 'Duration',
  SWITCH_MODEL: 'Switch model',
  SWITCH_TO_EMAIL_SEARCH: 'Switch to email search',
  SYSTEM_THEME_DESCRIPTION: 'Match your device theme',
  TOAST_FAILED_TO_CREATE_FOLDER_TITLE: 'Failed to create folder',
  TOAST_FAILED_TO_DELETE_FOLDER_TITLE: 'Failed to delete folder',
  TOOLKITS: 'Toolkits',
  TOOLS_NO_COMPOSIO_FOUND: 'No Composio tools found',
  TOOL_AUTH_UNAVAILABLE_HERE: 'This tool needs a sign-in that is not available here.',
  TOOL_CALL_COUNT_MANY: '{{count}} calls',
  TOOL_CALL_COUNT_ONE: '1 call',
  TOOL_CLIENT_SIGN_IN_HINT:
    'Everyone who uses this tool signs in with their own account through this app client.',
  TOOL_CREDENTIALS_REQUIRED_DESCRIPTION:
    'We are initiating a secure session. Enter your own credentials for this tool. They are sent securely, saved for you only, and never shared with the model.',
  TOOL_CREDENTIALS_SAVED_SUCCESS: 'Your credentials for {{name}} are saved.',
  TOOL_OIDC_CLIENT_REQUIRED_DESCRIPTION:
    'We are initiating a secure session. This tool signs you in with your own app registration at its identity provider. Enter its client ID, and its client secret if it has one. They are saved for you only and never shared with the model. You will be asked to sign in next.',
  TOOL_OWNER_SETUP_REQUIRED: "The tool owner needs to set up this tool's sign-in first.",
  TOPUP_NO_SPEND: 'No top-up credits were spent in this period.',
  TOPUP_SPENT_30_DAYS: 'Spent in the last 30 days',
  TOPUP_SPENT_LIFETIME: '{{credits}} credits spent in total',
  TOP_UP_CREDITS: 'Top up credits',
  TOP_UP_CREDITS_SECTION: 'Top-up credits (organization)',
  TRY_CONNECTING_AGAIN: 'The authorization did not complete. Please try again.',
  TYPE_YOUR_AGENT_GREETING: "Type your agent's greeting",
  UNABLE_TO_SAVE_TOOL: 'Unable to save the tool for this tenant.',
  UNTITLED_FOLDER: 'Untitled folder',
  UPDATE_FAILED: 'Update failed',
  UPGRADE_TO_GET_STARTED: 'Upgrade to get started',
  UPLOAD_SCHEMA_FILE: 'Upload schema file',
  USAGE_CONFIG_ACTIVE_LABEL: 'Organization active',
  USAGE_CONFIG_BLOCKED: 'Blocked',
  USAGE_CONFIG_BLOCKED_NOTE:
    'Nothing runs for this organization while it is blocked — every request is refused, whatever the meters below say.',
  USAGE_CONFIG_CATEGORIES_DESCRIPTION:
    'Group models that should share a limit. Give the group a key, then choose how much of each window that group can use.',
  USAGE_CONFIG_CATEGORIES_EMPTY: 'No categories yet. Nothing is restricted until you add one.',
  USAGE_CONFIG_CATEGORIES_LABEL: 'Use model categories',
  USAGE_CONFIG_CATEGORIES_OFF_NOTE:
    'Off: no category applies, and every allowed model can be used. This is the default, and most environments never change it.',
  USAGE_CONFIG_CATEGORIES_RESTRICTED_NOTE:
    'Model restriction is on, so a category can hold only the models picked there.',
  USAGE_CONFIG_CATEGORIES_TITLE: 'Model categories',
  USAGE_CONFIG_CATEGORY_ADD: 'Add category',
  USAGE_CONFIG_CATEGORY_ALSO_IN: 'also in {{name}}',
  USAGE_CONFIG_CATEGORY_DEFAULT_NAME: 'Category {{index}}',
  USAGE_CONFIG_CATEGORY_EDIT_MODELS: 'Edit the models in {{name}}',
  USAGE_CONFIG_CATEGORY_FILTER_MODELS: 'Filter models...',
  USAGE_CONFIG_CATEGORY_KEY_COPY: 'Copy the entitlement key',
  USAGE_CONFIG_CATEGORY_KEY_HINT:
    'The entitlement key this plan carries in Utilities, exactly as it is there',
  USAGE_CONFIG_CATEGORY_KEY_PLACEHOLDER: 'entitlement-key',
  USAGE_CONFIG_CATEGORY_MODELS_TITLE: 'Models in {{name}}',
  USAGE_CONFIG_CATEGORY_MORE: '+{{count}} more',
  USAGE_CONFIG_CHIP_INHERITED_TENANT: 'Tenant default',
  USAGE_CONFIG_CHOOSE_MODELS: 'Choose models',
  USAGE_CONFIG_DEFAULTS_NOTE: 'Showing defaults. Nothing has been saved for this environment yet.',
  USAGE_CONFIG_DEFAULTS_WARNING_BODY:
    'No usage config has been saved yet, so agents and chatbot widgets check the plan and refuse to answer without an active one. Turn enforcement off in Usage Config and nothing is limited or billed.',
  USAGE_CONFIG_DEFAULTS_WARNING_TITLE: 'Usage limits are enforced by default',
  USAGE_CONFIG_DEFAULT_ORGANIZATION: 'Default',
  USAGE_CONFIG_DESCRIPTION: 'Choose which plan limits this environment enforces.',
  USAGE_CONFIG_ENFORCED: 'Enforced',
  USAGE_CONFIG_ENFORCEMENT_LABEL: 'Enforce plan limits',
  USAGE_CONFIG_ENFORCEMENT_OFF_NOTE: 'Nothing is limited or billed while this is off.',
  USAGE_CONFIG_ENTITLEMENT_KEY: 'Entitlement key in Utilities',
  USAGE_CONFIG_FOLLOW_TENANT: 'Follow tenant',
  USAGE_CONFIG_GRANTS_NOTE:
    'Adding a category here gives it to nobody. The plan does that. The plan carries the category key, and Utilities answers whether it is granted.',
  USAGE_CONFIG_GRANTS_RULE:
    "A plan with a category key can use only that category's models. A plan with no category key can use every allowed model.",
  USAGE_CONFIG_INHERITED_FROM_TENANT: 'Follows tenant',
  USAGE_CONFIG_KEY_BLOCKS_SAVE: 'Fix the keys marked below before saving.',
  USAGE_CONFIG_KEY_EMPTY: 'A key is needed.',
  USAGE_CONFIG_KEY_TAKEN:
    'Already used by another window or category. Keys must be unique across both.',
  USAGE_CONFIG_LIMIT_WINDOWS_DESCRIPTION:
    'Each plan includes an amount for the billing period. A window limits how much of it can be used in a short time, such as any 5 hours or any one day, so it is not all spent at once. Turn a window on to set that limit. It resets on its own. If every window is off, only the billing period applies. Usage starts over when the next billing period begins.',
  USAGE_CONFIG_LIMIT_WINDOWS_TITLE: 'Limit windows',
  USAGE_CONFIG_LOAD_FAILED:
    'Usage config could not be loaded, so it cannot be changed. Reload the page to try again.',
  USAGE_CONFIG_METER_AI_CREDITS_DESCRIPTION: 'Every action that spends AI credits.',
  USAGE_CONFIG_METER_AI_CREDITS_LABEL: 'AI chat, summaries, embeddings',
  USAGE_CONFIG_METER_KEY_COLUMN: 'meterKey in Utilities',
  USAGE_CONFIG_METER_KEY_COPY: 'Copy the meter key',
  USAGE_CONFIG_METER_KEY_HINT: 'Meter key for the plan in Utilities',
  USAGE_CONFIG_METER_KNOWLEDGE_FOLDERS_DESCRIPTION:
    'How many knowledge folders this environment may hold.',
  USAGE_CONFIG_METER_KNOWLEDGE_FOLDERS_LABEL: 'Knowledge folders',
  USAGE_CONFIG_METER_LIBRARY_STORAGE_DESCRIPTION:
    'Storage used by uploaded files and library content.',
  USAGE_CONFIG_METER_LIBRARY_STORAGE_LABEL: 'Library storage',
  USAGE_CONFIG_MODELS_CUSTOM_EMPTY:
    'No custom model is validated and active yet. Add one on the Models page and it shows up here.',
  USAGE_CONFIG_MODELS_GROUP_BLOCKS_OF: 'Blocks {{type}} models',
  USAGE_CONFIG_MODELS_GROUP_CUSTOM: 'Custom models',
  USAGE_CONFIG_MODEL_PICKER_TITLE: 'Allowed models',
  USAGE_CONFIG_MODEL_RESTRICTION_COUNT_ONE: '{{count}} model allowed',
  USAGE_CONFIG_MODEL_RESTRICTION_COUNT_OTHER: '{{count}} models allowed',
  USAGE_CONFIG_MODEL_RESTRICTION_CUSTOM_EMPTY:
    'No active custom model yet. Add one on the Models page and it shows up here.',
  USAGE_CONFIG_MODEL_RESTRICTION_DESCRIPTION:
    'Choose which models can be used in this tenant, custom models included. Agents, chat, Smart, the widget and channels all follow it.',
  USAGE_CONFIG_MODEL_RESTRICTION_EDIT: 'Edit models',
  USAGE_CONFIG_MODEL_RESTRICTION_EMPTY:
    'Nothing is picked, so no model can be used until you pick one.',
  USAGE_CONFIG_MODEL_RESTRICTION_LABEL: 'Restrict which models may be used',
  USAGE_CONFIG_MODEL_RESTRICTION_OFF_NOTE:
    'Off: every model can be used, Blocks and custom. This is the default.',
  USAGE_CONFIG_MODEL_RESTRICTION_ON_NOTE:
    'On: only the models picked here can be used. A custom model is usable only after you add it here.',
  USAGE_CONFIG_MODEL_RESTRICTION_ORGANIZATION_DESCRIPTION:
    "Choose which models can be used in this organization. Its own list replaces the tenant's; switch it off here to allow every model, or follow the tenant.",
  USAGE_CONFIG_MODEL_RESTRICTION_PLAN_NOTE:
    "A plan can carry its own list of models. With this on, only models on both lists can be used; with it off, the plan's list is used. A model the plan pauses is taken out.",
  USAGE_CONFIG_MODEL_RESTRICTION_TITLE: 'Model restriction',
  USAGE_CONFIG_MODEL_TYPE_AUDIO: 'speech-to-text',
  USAGE_CONFIG_MODEL_TYPE_CHAT: 'chat',
  USAGE_CONFIG_MODEL_TYPE_EMBEDDING: 'embedding',
  USAGE_CONFIG_MODEL_TYPE_IMAGE: 'image',
  USAGE_CONFIG_MODEL_TYPE_RERANK: 'rerank',
  USAGE_CONFIG_MODEL_TYPE_TTS: 'text-to-speech',
  USAGE_CONFIG_NOT_ENFORCED: 'Not enforced',
  USAGE_CONFIG_NO_LIMIT_SKIP_CHECK: 'No limit — skip check',
  USAGE_CONFIG_NO_WINDOW_LIMIT: 'No extra limit',
  USAGE_CONFIG_OPEN_USAGE_CONFIG: 'Open Usage Config',
  USAGE_CONFIG_ORGANIZATION_DESCRIPTION: 'Choose which plan limits this organization enforces.',
  USAGE_CONFIG_ORGANIZATION_INHERIT_NOTE:
    'Anything left on follow keeps tracking the tenant. Only what you change here is saved for this organization.',
  USAGE_CONFIG_ORGANIZATION_SETUP_HINT:
    'Only the meters switched on here are checked. An organization you never edit follows the tenant exactly.',
  USAGE_CONFIG_OVERRIDDEN_HERE: 'Set here',
  USAGE_CONFIG_PAGE_SUBTITLE: 'Configure plan limits and organizational allowances',
  USAGE_CONFIG_SAVED: 'Usage config saved',
  USAGE_CONFIG_SAVE_FAILED: 'Could not save usage config',
  USAGE_CONFIG_SELECT_TO_CONFIGURE: 'Choose one from the list to configure it.',
  USAGE_CONFIG_SETTLEMENT_DESCRIPTION: 'Whose subscription pays.',
  USAGE_CONFIG_SETTLEMENT_ORG: 'Org wise (shared plan)',
  USAGE_CONFIG_SETTLEMENT_ORG_NOTE:
    'One subscription belongs to the organization and everyone draws on it -- the widget, channels and scheduled jobs included.',
  USAGE_CONFIG_SETTLEMENT_TITLE: 'Settlement strategy',
  USAGE_CONFIG_SETTLEMENT_USER: 'User wise (per seat plan)',
  USAGE_CONFIG_SETTLEMENT_USER_NOTE:
    'Each member holds their own subscription. One heavy user cannot empty the company. The widget is paid by whoever created it, a channel by whoever connected it, and a scheduled job by its owner.',
  USAGE_CONFIG_SETUP_HINT:
    'Only the meters switched on here are checked. If one is on but not on your plan in Utilities, those requests are blocked — copy its key from the row and add it to the plan.',
  USAGE_CONFIG_TAB_ORGANIZATIONS: 'Organizations',
  USAGE_CONFIG_TAB_TENANT: 'Tenant',
  USAGE_CONFIG_TENANT_ONLY_NOTE:
    "You set this once. Every organization uses the same settings and cannot change them. Only the amount differs, and that comes from each organization's own subscription.",
  USAGE_CONFIG_UNIT_BYTES: 'bytes',
  USAGE_CONFIG_UNIT_FOLDERS: 'folders',
  USAGE_CONFIG_UNIT_HINT: 'Plan quantity in {{unit}}',
  USAGE_CONFIG_WHAT_AGENTS_CHECK: 'What agents check',
  USAGE_CONFIG_WINDOW_ADD: 'Add window',
  USAGE_CONFIG_WINDOW_BURST: 'Burst factor',
  USAGE_CONFIG_WINDOW_BURST_SUFFIX: 'x',
  USAGE_CONFIG_WINDOW_CLAMPED:
    'This would be {{rawPercent}} of the billing period, so it is capped at the full plan.',
  USAGE_CONFIG_WINDOW_DAYS_SUFFIX: 'days',
  USAGE_CONFIG_WINDOW_DEFAULT_NAME: 'Window {{index}}',
  USAGE_CONFIG_WINDOW_EMPTY: "No windows yet. The subscription's billing period is the only limit.",
  USAGE_CONFIG_WINDOW_ENABLED: '{{name}} window',
  USAGE_CONFIG_WINDOW_FORMULA_DAILY:
    '{{burst}} × {{span}} ÷ {{workingDays}} = {{percent}} of the billing period',
  USAGE_CONFIG_WINDOW_FORMULA_HOURLY:
    '{{burst}} × {{hours}} ÷ ({{workingDays}} × 24) = {{percent}} of the billing period',
  USAGE_CONFIG_WINDOW_HOURS: 'Window length',
  USAGE_CONFIG_WINDOW_HOURS_SUFFIX: 'hours',
  USAGE_CONFIG_WINDOW_KEY_COPY: 'Copy the window key',
  USAGE_CONFIG_WINDOW_KEY_HINT: 'Follows the name until you type your own',
  USAGE_CONFIG_WINDOW_LENGTH_DAILY: 'Any {{days}} days',
  USAGE_CONFIG_WINDOW_LENGTH_HOURLY: 'Any {{hours}} hours',
  USAGE_CONFIG_WINDOW_LENGTH_ONE_DAY: 'Any one day',
  USAGE_CONFIG_WINDOW_LIMITS: 'Window limits',
  USAGE_CONFIG_WINDOW_OFF: 'Off',
  USAGE_CONFIG_WINDOW_OFF_NOTE: 'Off. No {{name}} cap.',
  USAGE_CONFIG_WINDOW_ON: 'On',
  USAGE_CONFIG_WINDOW_RENAME: 'Rename {{name}}',
  USAGE_CONFIG_WINDOW_SHARE_EDIT_FOR: 'Window limits for {{name}}',
  USAGE_CONFIG_WINDOW_SHARE_EMPTY:
    'No window is on, so there is nothing to limit here. Only the billing period applies.',
  USAGE_CONFIG_WINDOW_SHARE_NONE_SET: 'No limits',
  USAGE_CONFIG_WINDOW_SHARE_NOTE:
    'Check a window to limit this group inside it. Leave it unchecked and the group can use the whole window.',
  USAGE_CONFIG_WINDOW_SHARE_OF: '{{name}} - {{percent}}%',
  USAGE_CONFIG_WINDOW_SHARE_PERCENT: '{{name}} share',
  USAGE_CONFIG_WINDOW_SHARE_SUFFIX: '%',
  USAGE_CONFIG_WINDOW_SHARE_TITLE: 'Limits for {{name}}',
  USAGE_CONFIG_WINDOW_SPAN: 'Days per window',
  USAGE_CONFIG_WINDOW_TYPE_OF: '{{name}} type',
  USAGE_CONFIG_WINDOW_WORKING_DAYS: 'Working days',
  USE_OWN_TOOL_CLIENT: 'Use my own client instead of the configured one',
  VALID_EMAIL_REQUIRED: 'Please enter a valid email address',
  VERSION: 'Version',
  VOICE_CALL_START: 'Start voice call',
  VSDX: 'VSDX',
  WARNING: 'Warning',
  WEBSITE: 'Website',
  WEB_SEARCH: 'Web search',
  WEB_SEARCH_ACTION_DESCRIPTION:
    'Searches the web to improve response quality, especially for factual or news',
  WHEN_LIMITS_ARE_REACHED: 'When limits are reached',
  XLSX: 'XLSX',
  YOU_ARE_NOT_THE_OWNER_OF_THIS_RESOURCE: 'You are not the owner of this resource.',
};
